import {
    Avatar,
    Box,
    Flex,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Spinner,
    Text,
    VStack
} from '@chakra-ui/react'
import { FiChevronLeft, FiChevronRight, FiSearch } from 'react-icons/fi'
import {
    RegistryTeacher,
    STATUS_COLOR,
    STATUS_LABEL,
    TeacherStatus,
    getTeacherName,
    normalizeStatus
} from '../types'

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200]

export type StatusFilter = 'all' | TeacherStatus

const FILTERS: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'active', label: 'Active' },
    { value: 'suspended', label: 'Suspended' }
]

type Props = {
    teachers: RegistryTeacher[]
    total: number
    counts: Record<TeacherStatus, number>
    allCount: number
    status: StatusFilter
    onStatusChange: (status: StatusFilter) => void
    search: string
    onSearch: (value: string) => void
    selectedId: string | null
    onSelect: (id: string) => void
    page: number
    pageCount: number
    onPageChange: (page: number) => void
    pageSize: number
    onPageSizeChange: (size: number) => void
    loading?: boolean
}

export default function TeacherList({
    teachers,
    total,
    counts,
    allCount,
    status,
    onStatusChange,
    search,
    onSearch,
    selectedId,
    onSelect,
    page,
    pageCount,
    onPageChange,
    pageSize,
    onPageSizeChange,
    loading
}: Props) {
    return (
        <Flex direction="column" h="full">
            <Box p={3} borderBottomWidth="1px" borderColor="gray.100">
                <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                        <FiSearch color="gray" />
                    </InputLeftElement>
                    <Input
                        placeholder="Search teachers..."
                        value={search}
                        onChange={(event) => onSearch(event.target.value)}
                        borderRadius="md"
                    />
                </InputGroup>

                <Flex gap={1} mt={2} flexWrap="wrap">
                    {FILTERS.map((filter) => {
                        const count =
                            filter.value === 'all' ? allCount : counts[filter.value]
                        // Keep the chip row short: hide empty states unless selected.
                        if (
                            count === 0 &&
                            filter.value !== 'all' &&
                            status !== filter.value
                        )
                            return null
                        const isActive = status === filter.value
                        return (
                            <Box
                                as="button"
                                key={filter.value}
                                onClick={() => onStatusChange(filter.value)}
                                px={2}
                                py={1}
                                borderRadius="full"
                                borderWidth="1px"
                                fontSize="xs"
                                fontWeight={isActive ? 'semibold' : 'normal'}
                                borderColor={isActive ? 'brand.300' : 'gray.200'}
                                bg={isActive ? 'brand.50' : 'white'}
                                color={isActive ? 'brand.400' : 'gray.600'}
                                _hover={{ bg: isActive ? 'brand.50' : 'gray.50' }}
                                aria-pressed={isActive}
                            >
                                {filter.label} {count}
                            </Box>
                        )
                    })}
                </Flex>

                <Flex align="center" gap={2} mt={2}>
                    <Text fontSize="xs" color="gray.500">
                        {total} {total === 1 ? 'teacher' : 'teachers'}
                    </Text>
                    {loading ? <Spinner size="xs" color="gray.400" /> : null}
                </Flex>
            </Box>

            <VStack align="stretch" spacing={0} overflowY="auto" flex="1">
                {teachers.length === 0 ? (
                    <Text p={4} fontSize="sm" color="gray.500">
                        No teachers found.
                    </Text>
                ) : (
                    teachers.map((teacher) => {
                        const isActive = teacher._id === selectedId
                        const teacherStatus = normalizeStatus(teacher.status)
                        const name = getTeacherName(teacher)
                        return (
                            <Flex
                                as="button"
                                key={teacher._id}
                                textAlign="left"
                                align="center"
                                gap={3}
                                px={4}
                                py={3}
                                borderBottomWidth="1px"
                                borderColor="gray.50"
                                bg={isActive ? 'brand.50' : 'white'}
                                _hover={{ bg: isActive ? 'brand.50' : 'gray.50' }}
                                onClick={() => onSelect(teacher._id)}
                            >
                                <Avatar
                                    size="sm"
                                    name={name}
                                    src={teacher.picture ?? undefined}
                                    flexShrink={0}
                                />
                                <Box minW={0} flex="1">
                                    <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                                        {name}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                        {teacher.branch?.name || 'No branch'} ·{' '}
                                        {STATUS_LABEL[teacherStatus]}
                                    </Text>
                                </Box>
                                <Box
                                    boxSize="8px"
                                    borderRadius="full"
                                    flexShrink={0}
                                    bg={`${STATUS_COLOR[teacherStatus]}.400`}
                                    aria-hidden
                                />
                            </Flex>
                        )
                    })
                )}
            </VStack>

            <Flex
                align="center"
                justify="space-between"
                gap={2}
                px={2}
                py={2}
                borderTopWidth="1px"
                borderColor="gray.100"
            >
                <Select
                    size="xs"
                    value={pageSize}
                    onChange={(event) => onPageSizeChange(Number(event.target.value))}
                    w="auto"
                    borderRadius="md"
                    aria-label="Rows per page"
                >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                            {size} / page
                        </option>
                    ))}
                </Select>
                {pageCount > 1 ? (
                    <Flex align="center" gap={1}>
                        <IconButton
                            aria-label="Previous page"
                            icon={<FiChevronLeft />}
                            size="xs"
                            variant="ghost"
                            onClick={() => onPageChange(page - 1)}
                            isDisabled={page <= 1}
                        />
                        <Text fontSize="xs" color="gray.500">
                            Page {page} of {pageCount}
                        </Text>
                        <IconButton
                            aria-label="Next page"
                            icon={<FiChevronRight />}
                            size="xs"
                            variant="ghost"
                            onClick={() => onPageChange(page + 1)}
                            isDisabled={page >= pageCount}
                        />
                    </Flex>
                ) : null}
            </Flex>
        </Flex>
    )
}
